import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { EnvironmentContext } from "../EnvironmentContext";
import { useStoreBackend } from "../hooks/useStoreBackend";
import { StoreItem } from "../objects/StoreItem";

/**
 * Notification banner that shows when updates are available
 * Displays on the home page with a link to the updates page
 */
export const UpdateNotification = () => {
    const [updateCount, setUpdateCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [isDismissed, setIsDismissed] = useState<boolean>(false);
    const { environment } = useContext(EnvironmentContext);
    const auth = useAuth();
    const { t } = useTranslation();
    const sb = useStoreBackend();
    const location = useLocation();

    // Check if notification was previously dismissed in this session
    useEffect(() => {
        if (!environment) {
            setIsDismissed(false);
            return;
        }

        const dismissedData = sessionStorage.getItem('updateNotificationDismissed');
        if (dismissedData) {
            try {
                const { environment: savedEnvironment } = JSON.parse(dismissedData);
                
                // Show again only if environment changed
                if (savedEnvironment === environment) {
                    setIsDismissed(true);
                } else {
                    setIsDismissed(false);
                }
            } catch (e) {
                // Invalid data, clear it
                sessionStorage.removeItem('updateNotificationDismissed');
                setIsDismissed(false);
            }
        } else {
            setIsDismissed(false);
        }
    }, [environment]);

    useEffect(() => {
        if (!auth.isAuthenticated || !environment) {
            setLoading(false);
            setUpdateCount(0);
            return;
        }

        setLoading(true);
        sb.getStoreItems()
            .then(({ data, response }) => {
                const outdatedApps = (data as StoreItem[]).filter(
                    i => i.installedComputedVersion !== null && i.outdated
                );
                setUpdateCount(outdatedApps.length);
                setLoading(false);
            })
            .catch(() => {
                setUpdateCount(0);
                setLoading(false);
            });
    }, [environment, auth.isAuthenticated]);

    const handleDismiss = () => {
        if (!environment) return;
        
        setIsDismissed(true);
        sessionStorage.setItem('updateNotificationDismissed', JSON.stringify({
            environment: environment
        }));
    };

    // Don't show notification if loading, dismissed, not authenticated, no updates available, or not on root page
    if (loading || isDismissed || !auth.isAuthenticated || updateCount === 0 || location.pathname !== '/') {
        return null;
    }

    return (
        <div className="update-notification">
            <div className="update-notification-inner">
                <div className="container">
                    <div className="update-notification-content">
                        <i className="icon icon-info"></i>
                        <span className="update-notification-text">
                            {updateCount === 1 
                                ? t("UPDATE_NOTIFICATION_SINGLE")
                                : t("UPDATE_NOTIFICATION_MULTIPLE", { count: updateCount })
                            }
                            {" "}
                            <Link to="/updates" className="update-link">
                                {t("VIEW_UPDATES")}
                            </Link>
                        </span>
                        <button 
                            className="update-notification-close-btn" 
                            onClick={handleDismiss}
                            aria-label="Close notification"
                        >
                            ×
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
